import type { ViewGroup, ViewItem } from "@omnabase/ui";
import { SIDEBAR_VIEWS, UNKNOWN_ADDRESS_VIEW, type Sidebar, type SidebarItem, type Tag } from "@omnabase/shared";
import type { TicketCounts } from "../../lib/inbox";

/** The inbox's views column (split out of InboxScreen.tsx in the ticket page and marks round, 2026-10-23, so the "Unknown address" page shows
 *  the same column). Since the sidebar and consent round (2026-10-24) it draws the person's own layout (`me.sidebar`): the groups in their
 *  order with their names, hidden groups and entries left out, a folded group as its heading with the first entry's number (question 4) and
 *  marked current while the open view sits inside (question 6); Deleted and Unknown address only while they hold something or are on screen
 *  (question 3); a tag's entry only once the tags are known, and a ticket tag of another project not while a project is chosen. Pure. */
export type InboxViewsInput = {
  view: string; tagId: string | null; counts: TicketCounts | null; layout: Sidebar; tags: readonly Tag[]; projectId: string | null;
  viewHref: (id: string) => string; tagHref: (id: string) => string; onFold: (groupId: string) => void;
};

type Ctx = InboxViewsInput & { count: (id: string) => number | undefined };
const numberOf = (o: InboxViewsInput) => (id: string): number | undefined =>
  id === "deleted" ? o.counts?.deleted : id === UNKNOWN_ADDRESS_VIEW ? o.counts?.unknownAddress : (o.counts as Record<string, number | undefined> | null)?.[id];

function viewEntry(c: Ctx, id: string): ViewItem | null {
  const meta = SIDEBAR_VIEWS[id];
  if (!meta) return null;
  const active = id === c.view && !c.tagId;
  const n = meta.count ? c.count(id) : undefined;
  if (meta.onlyWhenFilled && !n && !active) return null;
  return { id, label: meta.label, icon: meta.icon as never, href: c.viewHref(id), active, count: n || undefined };
}
function tagEntry(c: Ctx, item: SidebarItem): ViewItem | null {
  const t = c.tags.find((x) => x.id === item.id && (item.kind === "customerTag" ? x.kind === "customer" : x.kind !== "customer"));
  if (!t || (item.kind === "tag" && c.projectId && t.projectId !== null && t.projectId !== c.projectId)) return null;
  return { id: `${item.kind}-${t.id}`, label: t.name, icon: item.kind === "customerTag" ? "user" : "tag", href: c.tagHref(t.id), active: c.tagId === t.id, count: c.counts?.tags[t.id] };
}
const entryOf = (c: Ctx, item: SidebarItem) => (item.kind === "view" ? viewEntry(c, item.id) : tagEntry(c, item));

export function inboxViewGroups(o: InboxViewsInput): ViewGroup[] {
  const c: Ctx = { ...o, count: numberOf(o) };
  return o.layout.groups.filter((g) => !g.hidden).map((g) => {
    const items = g.items.filter((i) => !i.hidden).map((i) => entryOf(c, i)).filter((x): x is ViewItem => !!x);
    return { label: g.name, items, fold: { folded: g.folded, onToggle: () => o.onFold(g.id), count: items[0]?.count, current: items.some((i) => i.active) } };
  });
}
